'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heading, Text, Button } from '@/components/ui/design-system';
import Link from 'next/link';
import { WaveShape } from '@/components/ui/WaveShape';
import { Check, ArrowRight, Sparkles, ChevronDown } from 'lucide-react';
import Image from 'next/image';
import { useMediaQuery } from '@/hooks/use-media-query';

const customizationOptions = [
  {
    name: 'Shape & Capacity',
    image: '/images/pages/home/customization/shape-&-capacity.jpg',
    icon: Check,
  },
  {
    name: 'Cap Types',
    image: '/images/pages/home/customization/cap-types.jpg',
    icon: Check,
  },
  {
    name: 'Accessories',
    image: '/images/pages/home/customization/accessories.jpg',
    icon: Check,
  },
  {
    name: 'Functional Customization',
    image: '/images/pages/home/customization/functional-customization.jpg',
    icon: Check,
  },
  {
    name: 'Outer Packaging',
    image: '/images/pages/home/customization/outer-packaging.jpg',
    icon: Check,
  },
  {
    name: 'Material Options',
    image: '/images/pages/home/customization/material.jpg',
    icon: Check,
  },
];

const fadeInStagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const fadeInItem = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 },
};

const defaultImage = {
  src: '/images/pages/home/customization/shape-&-capacity.jpg',
  alt: 'Default customization view showing a variety of bottle shapes and sizes.',
};

const floatingImages = [
  {
    src: '/images/pages/home/customization/cap-types.jpg',
    alt: 'Floating cap types',
    size: 80,
    className: 'absolute top-[12%] left-[3%] animate-float-strong opacity-80 hover:opacity-100',
    style: { animationDuration: '6s', animationDelay: '0s' },
  },
  {
    src: '/images/pages/home/customization/accessories.jpg',
    alt: 'Floating accessories',
    size: 90,
    className: 'absolute top-[25%] right-[5%] animate-float-gentle opacity-75 hover:opacity-100',
    style: { animationDuration: '8s', animationDelay: '1.5s' },
  },
  {
    src: '/images/pages/home/customization/material.jpg',
    alt: 'Floating material options',
    size: 75,
    className: 'absolute bottom-[35%] left-[8%] animate-float-bounce opacity-70 hover:opacity-100',
    style: { animationDuration: '7s', animationDelay: '3s' },
  },
  {
    src: '/images/pages/home/customization/outer-packaging.jpg',
    alt: 'Floating outer packaging',
    size: 85,
    className: 'absolute bottom-[15%] right-[3%] animate-float-strong opacity-80 hover:opacity-100',
    style: { animationDuration: '9s', animationDelay: '2s' },
  },
  {
    src: '/images/pages/home/customization/functional-customization.jpg',
    alt: 'Floating functional customization',
    size: 65,
    className: 'absolute top-[45%] left-[2%] animate-float-gentle opacity-60 hover:opacity-100',
    style: { animationDuration: '10s', animationDelay: '4s' },
  },
  {
    src: '/images/pages/home/customization/shape-&-capacity.jpg',
    alt: 'Floating shapes',
    size: 70,
    className: 'absolute top-[60%] right-[7%] animate-float-bounce opacity-65 hover:opacity-100',
    style: { animationDuration: '8.5s', animationDelay: '1s' },
  },
];

export function CustomizationShowcase() {
  const [activeOption, setActiveOption] = useState(customizationOptions[0]);
  const [hoveredImage, setHoveredImage] = useState<string | null>(null);
  const [expandedMobile, setExpandedMobile] = useState<string | null>(customizationOptions[0].name);
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  const displayedImage = hoveredImage || activeOption.image;

  const handleMobileClick = (option: typeof customizationOptions[0]) => {
    const newExpanded = expandedMobile === option.name ? null : option.name;
    setExpandedMobile(newExpanded);
    setActiveOption(option);
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-orange-50 via-red-50 to-orange-50 dark:bg-gradient-to-b dark:from-orange-900 dark:via-red-900 dark:to-orange-900 pt-20 sm:pt-28 pb-32 sm:pb-40">
      <WaveShape position="top" className="-top-1" />

      {/* Enhanced Floating Images for background texture */}
      <AnimatePresence>
        {isDesktop && floatingImages.map((img, index) => (
          <motion.div
            key={index}
            className={img.className}
            style={img.style}
            initial={{ opacity: 0, scale: 0.3, rotate: -10 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ 
              duration: 1.2, 
              delay: index * 0.3, 
              ease: [0.25, 0.46, 0.45, 0.94],
              type: 'spring',
              stiffness: 100
            }}
            whileHover={{ 
              scale: 1.1, 
              rotate: 5,
              transition: { duration: 0.3 }
            }}
          >
            <div 
              className="relative aspect-square group cursor-pointer" 
              style={{ width: img.size, height: img.size }}
            >
              <Image
                src={img.src}
                alt={img.alt}
                fill
                className="rounded-full object-cover shadow-xl transition-all duration-300 group-hover:shadow-2xl backdrop-blur-sm"
                style={{
                  filter: 'brightness(0.9) contrast(1.1)',
                  border: '3px solid rgba(255, 255, 255, 0.3)'
                }}
              />
              {/* Subtle glow effect */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-orange-200/20 to-red-200/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          </motion.div>
        ))}
      </AnimatePresence>

      <div className="container relative mx-auto flex flex-col items-center px-4">
        {/* Centered Header */}
        <motion.div
          className="mb-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Heading level={2} className="mb-4 text-primary-600 dark:text-white">
            Your Vision, Our Expertise
          </Heading>
          <Text className="max-w-2xl text-lg text-neutral-600 dark:text-white">
            From concept to creation, we offer a spectrum of customization options. Explore the possibilities below.
          </Text>
        </motion.div>

        {/* Interactive Layout Container */}
        <div className="grid w-full max-w-6xl grid-cols-1 gap-12 lg:grid-cols-10 lg:gap-16">
          {/* Left Column: Sticky Image on Desktop (60%) */}
          <motion.div
            className="hidden lg:block lg:sticky lg:col-span-6"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={displayedImage}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="absolute inset-0"
              >
                <div className="relative h-full w-full overflow-hidden rounded-2xl shadow-2xl">
                  <Image
                    src={displayedImage}
                    alt="Customization preview"
                    fill
                    className="object-cover"
                  />
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>

          {/* Right Column: Options List (40%) */}
          <motion.div
            className="flex w-full flex-col lg:col-span-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={{ visible: { transition: { staggerChildren: 0.07 } } }}
          >
            {customizationOptions.map((option) => {
              const isActive = activeOption.name === option.name;
              const isExpanded = expandedMobile === option.name;
              const Icon = option.icon;

              return (
                <motion.div
                  key={option.name}
                  variants={{ hidden: { opacity: 0, x: 20 }, visible: { opacity: 1, x: 0 } }}
                  whileHover={{ scale: 1.03, x: 5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  className="w-full border-b border-neutral-200/80 dark:border-neutral-800/80"
                  onMouseEnter={() => isDesktop && setHoveredImage(option.image)}
                  onMouseLeave={() => isDesktop && setHoveredImage(null)}
                  onClick={() => {
                    if (isDesktop) {
                      setActiveOption(option);
                    } else {
                      handleMobileClick(option);
                    }
                  }}
                >
                  <div className="flex w-full cursor-pointer items-center justify-between py-6">
                    <div className="flex items-center gap-4">
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-full transition-all duration-300 ${
                          isActive
                            ? 'bg-primary-500 text-white'
                            : 'bg-white text-neutral-600 border border-neutral-200/80 dark:bg-neutral-800 dark:text-neutral-100'
                        }`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <Text
                        size="xl"
                        weight="medium"
                        className={`transition-colors ${isActive ? 'text-primary-600 dark:text-white' : 'text-neutral-700 dark:text-white'}`}>
                        {option.name}
                      </Text>
                    </div>
                    <ChevronDown
                      className={`h-6 w-6 text-neutral-500 dark:text-white transition-transform duration-300 lg:hidden ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </div>

                  {/* Mobile Image Expansion */}
                  <AnimatePresence>
                    {!isDesktop && isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0, marginTop: 0 }}
                        animate={{ height: 'auto', opacity: 1, marginTop: '-0.5rem', marginBottom: '1.5rem' }}
                        exit={{ height: 0, opacity: 0, marginTop: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="relative aspect-video w-full overflow-hidden rounded-lg">
                          <Image src={option.image} alt={option.name} fill className="object-cover" />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        {/* Centered CTA Button */}
        <div className="mt-16">
          <Button asChild size="xl" className="group premium-gradient-primary text-white">
            <Link href="/about" className="flex items-center space-x-2">
              <span>Dive into the world of customization</span>
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
      <WaveShape position="bottom" className="-bottom-1" />
    </section>
  );
}
